import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FINDING_PAGE_SIZE,
  useAllFindings,
  useCollections,
  useCoverage,
  useCreateCollection,
  useEpisodes,
  useFindingPages,
  useProjects,
  useReport,
} from '../api/queries';
import { SourcePicker } from '../components/SourcePicker';
import { revealInStrip } from '../lib/focus';
import type { Episode, Finding, FindingFilters, ResearchCollection } from '../api/types';
import { EvidenceMatrix, SourceCoverageMatrix } from '../components/Coverage';
import { EvidenceTallyLine, tallyEvidence } from '../components/Evidence';
import { FindingCard, findingPath } from '../components/FindingCard';
import { Field, MutationError } from '../components/Form';
import { ExplanationBlock } from '../components/Prose';
import { EmptyState, ErrorState, Loading, QueryState } from '../components/States';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate, formatRange, plural, relationMeta, sourceKindLabel, statusMeta, themeLabel, yearOf } from '../lib/format';

const FILTER_KEYS = ['q', 'project', 'theme', 'status', 'from', 'to', 'source', 'evidenceType'] as const;
const VIEWS = [
  ['findings', 'Findings'],
  ['timeline', 'Timeline'],
  ['compare', 'Compare'],
  ['coverage', 'Coverage'],
] as const;
type View = (typeof VIEWS)[number][0];
const MAX_COMPARE = 3;

function readFilters(params: URLSearchParams): FindingFilters {
  const filters: FindingFilters = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  return filters;
}

function FilterBar({ filters, onChange }: { filters: FindingFilters; onChange: (next: FindingFilters) => void }) {
  const projects = useProjects();
  const all = useAllFindings({});
  const themes = useMemo(() => [...new Set((all.data?.items ?? []).flatMap((finding) => finding.themes))].sort(), [all.data]);
  const [q, setQ] = useState(filters.q ?? '');

  const set = (key: keyof FindingFilters, value: string) => {
    const next = { ...filters };
    if (value === '') delete next[key];
    else next[key] = value;
    onChange(next);
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    set('q', q.trim());
  }

  const active = FILTER_KEYS.filter((key) => filters[key] !== undefined).length;

  return (
    <form className="filters" role="search" aria-label="Search and filter findings" onSubmit={submit}>
      <div className="filters__search">
        <label className="field__label" htmlFor="explore-q">
          Search findings
        </label>
        <div className="search-row">
          <input
            id="explore-q"
            type="search"
            className="input"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Title, summary, interpretation or theme"
          />
          <button type="submit" className="button button--primary">
            Search
          </button>
        </div>
      </div>
      <div className="filters__grid">
        <label className="field">
          <span className="field__label">Project</span>
          <select className="input" value={filters.project ?? ''} onChange={(event) => set('project', event.target.value)}>
            <option value="">All projects</option>
            {(projects.data?.items ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Theme</span>
          <select className="input" value={filters.theme ?? ''} onChange={(event) => set('theme', event.target.value)}>
            <option value="">All themes</option>
            {themes.map((theme) => (
              <option key={theme} value={theme}>
                {themeLabel(theme)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Status</span>
          <select className="input" value={filters.status ?? ''} onChange={(event) => set('status', event.target.value)}>
            <option value="">Any status</option>
            {Object.entries(statusMeta).map(([status, meta]) => (
              <option key={status} value={status}>
                {meta.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Evidence type</span>
          <select className="input" value={filters.evidenceType ?? ''} onChange={(event) => set('evidenceType', event.target.value)}>
            <option value="">Any evidence</option>
            <optgroup label="Relation">
              {Object.entries(relationMeta).map(([relation, meta]) => (
                <option key={relation} value={relation}>
                  {meta.plural}
                </option>
              ))}
            </optgroup>
            <optgroup label="Source kind">
              {Object.entries(sourceKindLabel).map(([kind, label]) => (
                <option key={kind} value={kind}>
                  {label}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
        <div className="field field--period">
          <span className="field__label" id="period-label">
            Source cutoff between
          </span>
          <div className="period" role="group" aria-labelledby="period-label">
            <input
              type="date"
              className="input"
              aria-label="From"
              value={filters.from ?? ''}
              onChange={(event) => set('from', event.target.value)}
            />
            <input type="date" className="input" aria-label="To" value={filters.to ?? ''} onChange={(event) => set('to', event.target.value)} />
          </div>
        </div>
      </div>
      <SourcePicker
        label="Source"
        value={filters.source ?? ''}
        emptyLabel="Any source"
        help="Searches the whole archive you can see, a page at a time."
        onChange={(sourceId) => set('source', sourceId)}
      />
      {active > 0 && (
        <p className="filters__active">
          {plural(active, 'filter')} active.{' '}
          <button
            type="button"
            className="button button--link"
            onClick={() => {
              setQ('');
              onChange({});
            }}
          >
            Clear all
          </button>
        </p>
      )}
    </form>
  );
}

function SelectBox({ finding, selected, onToggle }: { finding: Finding; selected: boolean; onToggle: () => void }) {
  return (
    <label className="checkbox checkbox--card">
      <input type="checkbox" checked={selected} onChange={onToggle} />
      <span className="visually-hidden">Select “{finding.title}” for a collection or comparison</span>
    </label>
  );
}

function FindingsView({
  findings,
  selected,
  toggle,
  projectTitles,
}: {
  findings: Finding[];
  selected: string[];
  toggle: (id: string) => void;
  projectTitles: Map<string, string>;
}) {
  if (findings.length === 0) {
    return (
      <EmptyState title="No findings match these filters">
        <p>Try removing a filter. Restricted findings are never counted here.</p>
      </EmptyState>
    );
  }
  return (
    <ul className="card-list">
      {findings.map((finding) => (
        <li key={finding.id}>
          <FindingCard
            finding={finding}
            projectTitle={finding.projectId === undefined ? undefined : projectTitles.get(finding.projectId)}
            select={<SelectBox finding={finding} selected={selected.includes(finding.id)} onToggle={() => toggle(finding.id)} />}
          />
        </li>
      ))}
    </ul>
  );
}

function TimelineView({ findings, filters }: { findings: Finding[]; filters: FindingFilters }) {
  const episodes = useEpisodes();
  if (episodes.isPending) return <Loading label="Loading episodes" />;
  if (episodes.isError) return <ErrorState error={episodes.error} onRetry={() => void episodes.refetch()} />;
  const linked = new Set(findings.flatMap((finding) => finding.episodeIds));
  const findingByEpisode = new Map<string, Finding[]>();
  for (const finding of findings) {
    for (const id of finding.episodeIds) findingByEpisode.set(id, [...(findingByEpisode.get(id) ?? []), finding]);
  }
  const filtering = Object.keys(filters).length > 0;
  const items = episodes.data.items.filter((episode) => !filtering || linked.has(episode.id));
  const groups = new Map<string, Episode[]>();
  for (const episode of items) {
    const year = yearOf(episode.startAt ?? episode.endAt);
    const key = year === null ? 'undated' : String(year);
    groups.set(key, [...(groups.get(key) ?? []), episode]);
  }
  const years = [...groups.keys()].filter((key) => key !== 'undated').sort();
  if (groups.has('undated')) years.push('undated');
  if (items.length === 0) {
    return (
      <EmptyState title="No episodes for these filters">
        <p>Episodes appear here when a visible finding links to them.</p>
      </EmptyState>
    );
  }
  return (
    <div className="year-timeline">
      <p className="muted">
        Episodes are placed by the earliest date the sources establish. Undated episodes are kept together rather than
        guessed into a year.
      </p>
      {years.map((year) => (
        <section key={year} className="year" aria-labelledby={`year-${year}`}>
          <h3 id={`year-${year}`} className="year__label">
            {year === 'undated' ? 'Date not established' : year}
          </h3>
          <ol className="year__episodes">
            {(groups.get(year) ?? []).map((episode) => (
              <li key={episode.id} className="year-episode">
                <p className="year-episode__date">{formatRange(episode.startAt, episode.endAt)}</p>
                <p className="year-episode__title">{episode.title}</p>
                <p className="year-episode__summary">{episode.summary}</p>
                <EvidenceTallyLine evidence={episode.evidence} />
                {(findingByEpisode.get(episode.id) ?? []).map((finding) => (
                  <Link key={finding.id} to={findingPath(finding.id)} className="text-link">
                    {finding.title} →
                  </Link>
                ))}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function CompareView({ findings, selected }: { findings: Finding[]; selected: string[] }) {
  const chosen = selected
    .map((id) => findings.find((finding) => finding.id === id))
    .filter((finding): finding is Finding => finding !== undefined)
    .slice(0, MAX_COMPARE);
  if (chosen.length < 2) {
    return (
      <EmptyState title="Select two or three findings to compare">
        <p>Tick findings in the Findings view. The comparison places their scope, evidence and uncertainty side by side.</p>
      </EmptyState>
    );
  }
  const rows: [string, (finding: Finding) => React.ReactNode][] = [
    ['Status', (finding) => <StatusBadge status={finding.status} needsReview={finding.needsReview} />],
    ['Summary', (finding) => finding.summary],
    ['Scope', (finding) => finding.scope],
    ['Evidence', (finding) => {
      const tally = tallyEvidence(finding.evidence);
      return `${String(tally.supports)} supporting · ${String(tally.contradicts)} conflicting · ${String(tally.context)} context`;
    }],
    ['Interpretation', (finding) => finding.interpretation],
    ['Uncertainty', (finding) => <ExplanationBlock value={finding.uncertainty} empty="None recorded." />],
    ['Sources to', (finding) => formatDate(finding.sourceCutoff)],
  ];
  return (
    <div className="matrix-wrap" tabIndex={0} role="region" aria-label="Side-by-side comparison">
      <table className="compare">
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Aspect</span>
            </th>
            {chosen.map((finding) => (
              <th key={finding.id} scope="col">
                <Link to={findingPath(finding.id)}>{finding.title}</Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, render]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              {chosen.map((finding) => (
                <td key={finding.id}>{render(finding)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoverageView({ findings }: { findings: Finding[] }) {
  const coverage = useCoverage();
  const report = useReport();
  const chapters = (report.data?.chapters ?? []).map((chapter) => ({ id: chapter.id, title: chapter.title }));
  return (
    <div className="coverage-view">
      <section aria-labelledby="evidence-matrix-title">
        <h3 id="evidence-matrix-title" className="section-title">
          What kinds of evidence the findings rest on
        </h3>
        <EvidenceMatrix findings={findings} chapters={chapters} />
      </section>
      <section aria-labelledby="source-matrix-title">
        <h3 id="source-matrix-title" className="section-title">
          What has been imported
        </h3>
        <QueryState query={coverage} label="coverage">
          {(data) => <SourceCoverageMatrix coverage={data} />}
        </QueryState>
      </section>
    </div>
  );
}

function CollectionsPanel({
  selected,
  onOpen,
  onClear,
}: {
  selected: string[];
  onOpen: (collection: ResearchCollection) => void;
  onClear: () => void;
}) {
  const collections = useCollections();
  const create = useCreateCollection();
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [errors, setErrors] = useState<{ title?: string; question?: string }>({});
  const [saved, setSaved] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next: typeof errors = {};
    if (title.trim() === '') next.title = 'Name the collection.';
    if (question.trim() === '') next.question = 'State the research question this collection helps answer.';
    setErrors(next);
    if (Object.keys(next).length > 0 || selected.length === 0) return;
    create.mutate(
      { title: title.trim(), researchQuestion: question.trim(), findingIds: selected },
      {
        onSuccess: () => {
          setSaved(title.trim());
          setTitle('');
          setQuestion('');
        },
      },
    );
  }

  return (
    <aside className="collections" aria-labelledby="collections-title">
      <h2 id="collections-title" className="panel__title">
        Research collections
      </h2>
      <p className="muted small">
        {selected.length === 0
          ? 'Tick findings to start a collection or a comparison.'
          : `${plural(selected.length, 'finding')} selected.`}{' '}
        {selected.length > 0 && (
          <button type="button" className="button button--link" onClick={onClear}>
            Clear selection
          </button>
        )}
      </p>
      {selected.length > 0 && (
        <form className="form" onSubmit={submit} noValidate>
          <Field label="Collection name" error={errors.title ?? null}>
            {(props) => <input {...props} className="input" value={title} onChange={(event) => setTitle(event.target.value)} />}
          </Field>
          <Field label="Research question" error={errors.question ?? null} help="What are you trying to understand?">
            {(props) => (
              <textarea {...props} className="input textarea" rows={3} value={question} onChange={(event) => setQuestion(event.target.value)} />
            )}
          </Field>
          <MutationError error={create.error} />
          <button type="submit" className="button button--primary" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Save collection'}
          </button>
        </form>
      )}
      {saved !== null && (
        <p className="form-success" role="status">
          Saved “{saved}”.
        </p>
      )}
      <h3 className="minor-title">Your saved collections</h3>
      {collections.isPending && <Loading label="Loading collections" />}
      {collections.isError && <ErrorState error={collections.error} onRetry={() => void collections.refetch()} />}
      {collections.data !== undefined &&
        (collections.data.items.length === 0 ? (
          <p className="muted small">None yet.</p>
        ) : (
          <ul className="collection-list">
            {collections.data.items.map((collection) => (
              <li key={collection.id} className="collection">
                <p className="collection__title">{collection.title}</p>
                <p className="collection__question">{collection.researchQuestion}</p>
                <button type="button" className="button button--link" onClick={() => onOpen(collection)}>
                  Open {plural(collection.findingIds.length, 'finding')}
                </button>
              </li>
            ))}
          </ul>
        ))}
    </aside>
  );
}

export function Explore() {
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const view = (VIEWS.some(([key]) => key === params.get('view')) ? params.get('view') : 'findings') as View;
  const selected = (params.get('sel') ?? '').split(',').filter((id) => id !== '');
  const findings = useFindingPages(filters);
  const loaded = findings.data?.pages.flatMap((page) => page.items) ?? [];
  const total = findings.data?.pages[0]?.total ?? loaded.length;
  const complete = !findings.hasNextPage;
  const [loadingAll, setLoadingAll] = useState(false);

  async function loadAll() {
    setLoadingAll(true);
    try {
      let result = await findings.fetchNextPage();
      while (result.hasNextPage && !result.isError) result = await findings.fetchNextPage();
    } finally {
      setLoadingAll(false);
    }
  }
  const projects = useProjects();
  const projectTitles = new Map((projects.data?.items ?? []).map((project) => [project.id, project.title]));

  const update = (next: FindingFilters, extra: Record<string, string | null> = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(next)) if (value !== undefined && value !== '') search.set(key, value);
    const merged = { view: view === 'findings' ? null : view, sel: selected.length > 0 ? selected.join(',') : null, ...extra };
    for (const [key, value] of Object.entries(merged)) if (value !== null && value !== '') search.set(key, value);
    setParams(search, { replace: true });
  };

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id];
    update(filters, { sel: next.join(',') });
  };

  return (
    <main id="main" tabIndex={-1} className="page page--wide">
      <header className="page__header">
        <p className="eyebrow">Explore</p>
        <h1 className="page__title">Search the evidence</h1>
        <p className="lede">
          Filter every finding you can see by period, project, theme, source and kind of evidence. Save sets of findings
          as collections around a research question, or compare them side by side. For the current project loop, see the{' '}
          <Link to="/pilots">selected pilots</Link>.
        </p>
      </header>
      <FilterBar key={params.get('q') ?? ''} filters={filters} onChange={(next) => update(next)} />

      <div className="explore">
        <div className="explore__main">
          <div className="tabs" role="tablist" aria-label="Ways to view the results">
            {VIEWS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                id={`tab-${key}`}
                aria-selected={view === key}
                aria-controls="explore-panel"
                tabIndex={view === key ? 0 : -1}
                className={view === key ? 'tab tab--active' : 'tab'}
                onClick={() => update(filters, { view: key === 'findings' ? null : key })}
                // A selected tab near the strip's edge is revealed whole when it takes focus.
                onFocus={revealInStrip}
                onKeyDown={(event) => {
                  // Roving tabs: arrows wrap; Home and End jump to the first and last tab.
                  const index = VIEWS.findIndex(([candidate]) => candidate === key);
                  let next: number | null = null;
                  if (event.key === 'ArrowRight') next = (index + 1) % VIEWS.length;
                  else if (event.key === 'ArrowLeft') next = (index - 1 + VIEWS.length) % VIEWS.length;
                  else if (event.key === 'Home') next = 0;
                  else if (event.key === 'End') next = VIEWS.length - 1;
                  if (next === null) return;
                  event.preventDefault();
                  const target = VIEWS[next];
                  if (target === undefined) return;
                  update(filters, { view: target[0] === 'findings' ? null : target[0] });
                  document.getElementById(`tab-${target[0]}`)?.focus();
                }}
              >
                {label}
                {key === 'compare' && selected.length > 0 && <span className="tab__count">{Math.min(selected.length, MAX_COMPARE)}</span>}
              </button>
            ))}
          </div>
          <div id="explore-panel" role="tabpanel" aria-labelledby={`tab-${view}`} className="explore__panel">
            {findings.isPending && <Loading label="Loading findings" />}
            {findings.isError && <ErrorState error={findings.error} onRetry={() => void findings.refetch()} />}
            {findings.data !== undefined && (
              <>
                <p className="result-count" role="status" aria-live="polite">
                  {complete
                    ? `${plural(total, 'finding')} match.`
                    : `Showing ${String(loaded.length)} of ${String(total)} matching findings.`}
                </p>
                {!complete && view !== 'findings' && (
                  <p className="notice notice--proposed" role="note">
                    This view uses the {loaded.length} findings loaded so far, not all {total}.{' '}
                    <button type="button" className="button button--link" disabled={loadingAll} onClick={() => void loadAll()}>
                      {loadingAll ? 'Loading…' : `Load all ${String(total)}`}
                    </button>
                  </p>
                )}
                {view === 'findings' && <FindingsView findings={loaded} selected={selected} toggle={toggle} projectTitles={projectTitles} />}
                {view === 'timeline' && <TimelineView findings={loaded} filters={filters} />}
                {view === 'compare' && <CompareView findings={loaded} selected={selected} />}
                {view === 'coverage' && <CoverageView findings={loaded} />}
                {view === 'findings' && findings.hasNextPage && (
                  <div className="button-row load-more">
                    <button
                      type="button"
                      className="button button--secondary"
                      disabled={findings.isFetchingNextPage || loadingAll}
                      onClick={() => void findings.fetchNextPage()}
                    >
                      {findings.isFetchingNextPage
                        ? 'Loading…'
                        : `Load ${String(Math.min(FINDING_PAGE_SIZE, total - loaded.length))} more findings`}
                    </button>
                    <button type="button" className="button button--link" disabled={loadingAll} onClick={() => void loadAll()}>
                      {loadingAll ? 'Loading all…' : `Load all ${String(total)}`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <CollectionsPanel
          selected={selected}
          onClear={() => update(filters, { sel: null })}
          onOpen={(collection) => {
            const search = new URLSearchParams({ sel: collection.findingIds.join(',') });
            setParams(search, { replace: false });
          }}
        />
      </div>
    </main>
  );
}
